import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { healthcareApi, type PersonDetailResponse } from '../api/client';
import { useFormDraft, useBeforeUnloadWarning } from '../hooks/useFormDraft';
import { toConsentBlock, type ConsentBlockState } from '../utils/healthcareConsent';
import { useInterviewRecordOptions } from '../hooks/useInterviewRecordOptions';
import InterviewRecordsSection from '../components/InterviewRecordsSection';
import InterviewScheduleList from '../components/InterviewScheduleList';

/** datetime-local の value 用に ISO 文字列を YYYY-MM-DDTHH:mm に変換 */
function toDateTimeLocal(iso: string) {
  try {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}`;
  } catch {
    return '';
  }
}

/** HLT-S03 面談設定（API 連携・新規作成） */
export default function HealthcareInterview() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [scheduledAt, setScheduledAt] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return toDateTimeLocal(d.toISOString());
  });
  const [location, setLocation] = useState('');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [notifyUser, setNotifyUser] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [person, setPerson] = useState<PersonDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * 同意起因で操作できない状態（課題 #26 / 案1）。
   * 個人詳細（HC-S02）と同じ扱いに揃える。以前は BE が CONSENT_REQUIRED を返しても
   * 「読み込みエラー」と表示され、同意が理由だと分からなかった。
   */
  const [consentBlock, setConsentBlock] = useState<ConsentBlockState | null>(null);
  /** 面談記録の項目（課題 #71 / BE PR #143）。options API が無い古い BE では記録の欄を出さない */
  const recordOptions = useInterviewRecordOptions();

  // ドラフト保護（2026-05-31）: 面談メモなど手入力が消えるリスクへの対策。
  const draftKey = `healthcare/interview/${id ?? 'unknown'}`;
  const draftValue = useMemo(
    () => ({ scheduledAt, location, meetingUrl, notes, notifyUser }),
    [scheduledAt, location, meetingUrl, notes, notifyUser]
  );
  const { clearDraft } = useFormDraft(draftKey, draftValue, (d) => {
    if (typeof d?.scheduledAt === 'string') setScheduledAt(d.scheduledAt);
    if (typeof d?.location === 'string') setLocation(d.location);
    if (typeof d?.meetingUrl === 'string') setMeetingUrl(d.meetingUrl);
    if (typeof d?.notes === 'string') setNotes(d.notes);
    if (typeof d?.notifyUser === 'boolean') setNotifyUser(d.notifyUser);
  }, { enabled: !loading });
  const isDirty = Boolean(notes.trim() || location.trim() || meetingUrl.trim());
  useBeforeUnloadWarning(isDirty && !saving);

  useEffect(() => {
    const token = getToken();
    if (!token || !id) {
      setLoading(false);
      return;
    }
    setLoadError(null);
    setConsentBlock(null);
    setLoading(true);
    healthcareApi
      .getPerson(id, token)
      .then((data) => {
        setPerson(data);
      })
      .catch((err) => {
        // 同意起因なら専用表示。それ以外だけ通常のエラー表示にする
        const blocked = toConsentBlock(err);
        if (blocked) {
          setConsentBlock(blocked);
          return;
        }
        setLoadError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
      })
      .finally(() => setLoading(false));
  }, [id, getToken, t]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token || !id || !scheduledAt.trim()) return;
    setSaveError(null);
    setSaving(true);
    const scheduledAtIso = new Date(scheduledAt).toISOString();
    healthcareApi
      .createInterview(
        {
          user_id: id,
          scheduled_at: scheduledAtIso,
          location: location.trim() || undefined,
          meeting_url: meetingUrl.trim() || undefined,
          notes: notes.trim() || undefined,
          notify_user: notifyUser,
        },
        token
      )
      .then(() => {
        clearDraft();
        navigate(`/healthcare/person/${id}`);
      })
      .catch((err) => {
        // 同意が取り下げられた等で BE が止めた場合（#71 / BE PR #131）は、同意が必要な画面に切り替える
        const blocked = toConsentBlock(err);
        if (blocked) {
          setConsentBlock(blocked);
          setSaving(false);
          return;
        }
        setSaveError(err instanceof Error ? err.message : t('common.error'));
        setSaving(false);
      });
  };

  if (loading) {
    return (
      <div className="page healthcare-interview-page">
        <h1>{t('healthcare.interviews')}</h1>
        <p className="healthcare-anon-label">対象: {id ?? '—'}</p>
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  /*
   * 同意起因で BE が弾いた場合（課題 #26 / 案1）。
   * 個人詳細（HC-S02）と同じ体裁・同じ情報量で表示し、
   * 「なぜ操作できないのか」が面談設定側でも分かるようにする。
   *
   * 読み込みエラーの分岐より前で判定すること。同意が無いと個人データ（person）が
   * 取れないため、後ろに置くと「データの取得に失敗しました」が出てしまう
   * （2026-09-29 高尾さんのご指摘。#77）。
   */
  if (consentBlock) {
    return (
      <div className="page healthcare-interview-page">
        <h1>{t('healthcare.interviews')}</h1>
        <p className="healthcare-anon-label">{t('healthcare.anonId')}: {id ?? '—'}</p>
        <div className="healthcare-card">
          {consentBlock.kind === 'anonymous_restricted' ? (
            <p className="healthcare-error-text">{t('healthcare.anonymousModeRestricted')}</p>
          ) : (
            <>
              <p className="healthcare-error-text">{t('healthcare.consentRequiredHeader')}</p>
              {consentBlock.missing.length > 0 && (
                <ul className="healthcare-missing-consents-list">
                  {consentBlock.missing.map((m, i) => (
                    <li key={`${m.purpose}-${m.data_type}-${i}`}>{m.label_ja}</li>
                  ))}
                </ul>
              )}
              <p className="healthcare-consent-explain">{t('healthcare.consentRequiredHint')}</p>
            </>
          )}
          <p className="healthcare-consent-gate-note">{t('healthcare.interviewBlockedByConsent')}</p>
        </div>
        <p className="healthcare-back">
          <Link to={id ? `/healthcare/person/${id}` : '/healthcare'}>
            {t('common.back')} → {id ? t('healthcare.backToPerson') : t('healthcare.backToCandidates')}
          </Link>
        </p>
      </div>
    );
  }

  if (loadError || !person) {
    return (
      <div className="page healthcare-interview-page">
        <h1>{t('healthcare.interviews')}</h1>
        <p className="healthcare-anon-label">対象: {id ?? '—'}</p>
        <p className="healthcare-error-text">{loadError ?? t('dashboard.errorLoad')}</p>
        <p className="healthcare-back">
          <Link to={id ? `/healthcare/person/${id}` : '/healthcare'}>
            {t('common.back')} → {id ? t('healthcare.backToPerson') : t('healthcare.backToCandidates')}
          </Link>
        </p>
      </div>
    );
  }

  if (!person.consent_granted) {
    return (
      <div className="page healthcare-interview-page">
        <h1>{t('healthcare.interviews')}</h1>
        <p className="healthcare-anon-label">対象: {id ?? '—'}</p>
        <div className="healthcare-card">
          <p className="healthcare-consent-explain">{t('healthcare.consentNoneExplain')}</p>
          <p className="healthcare-consent-gate-note">{t('healthcare.interviewBlockedByConsent')}</p>
        </div>
        <p className="healthcare-back">
          <Link to={id ? `/healthcare/person/${id}` : '/healthcare'}>
            {t('common.back')} → {id ? t('healthcare.backToPerson') : t('healthcare.backToCandidates')}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="page healthcare-interview-page">
      <h1>{t('healthcare.interviews')}</h1>
      <p className="healthcare-anon-label">対象: {id ?? '—'}</p>

      {id && <InterviewScheduleList personId={id} />}

      <form onSubmit={handleSubmit} className="healthcare-card healthcare-form">
        <h3>{t('healthcare.interviewCreate')}</h3>
        <div className="healthcare-form-group">
          <label htmlFor="interview-scheduled-at">{t('healthcare.interviewDateTime')}</label>
          <input
            id="interview-scheduled-at"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            required
          />
        </div>
        <div className="healthcare-form-group">
          <label htmlFor="interview-location">{t('healthcare.interviewLocation')}</label>
          <input id="interview-location" type="text" placeholder={t('healthcare.interviewLocationPlaceholder')} value={location} onChange={(e) => setLocation(e.target.value)} />
        </div>
        <div className="healthcare-form-group">
          <label htmlFor="interview-meeting-url">{t('healthcare.interviewMeetingUrl')}</label>
          <input id="interview-meeting-url" type="url" placeholder="https://..." value={meetingUrl} onChange={(e) => setMeetingUrl(e.target.value)} />
        </div>
        <div className="healthcare-form-group">
          <label htmlFor="interview-notes">{t('healthcare.interviewNotes')}</label>
          <textarea id="interview-notes" rows={3} placeholder={t('healthcare.interviewNotesPlaceholder')} value={notes} onChange={(e) => setNotes(e.target.value)} className="healthcare-textarea" />
        </div>
        <div className="healthcare-form-group healthcare-checkbox">
          <label>
            <input type="checkbox" checked={notifyUser} onChange={(e) => setNotifyUser(e.target.checked)} />
            {t('healthcare.interviewNotify')}
          </label>
        </div>
        {saveError && <p className="healthcare-form-error">{saveError}</p>}
        <button type="submit" className="healthcare-btn-primary" disabled={saving}>
          {saving ? t('common.loading') : t('healthcare.interviewSave')}
        </button>
      </form>

      {recordOptions.status === 'ready' && id && (
        <InterviewRecordsSection personId={id} options={recordOptions.options} onConsentBlocked={setConsentBlock} />
      )}
      {recordOptions.status === 'error' && (
        <div className="healthcare-card" role="alert">
          <p className="healthcare-error-text">{t('healthcare.records.optionsError')}</p>
        </div>
      )}

      <p className="healthcare-back">
        <Link to={id ? `/healthcare/person/${id}` : '/healthcare'}>
          {t('common.back')} → {id ? t('healthcare.backToPerson') : t('healthcare.backToCandidates')}
        </Link>
      </p>
    </div>
  );
}
